import { useCallback, useEffect, useMemo, useState } from "react";
import { TableSkeleton } from "../../components/Loading";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supplierFinanceApi, type SupplierFinanceDetail as Detail } from "../../api/supplierFinance";
import { useAuth } from "../../auth/AuthContext";
import { ExportButton } from "../../components/ExportButton";
import type { ApiError } from "../../api/client";
import { FinanceDetail } from "../finance/FinanceDetail";
import { FinanceFilters, type FiltersValue } from "../finance/FinanceFilters";
import { presetRange } from "../finance/usePeriod";

/** Internal drill-down into one supplier company's finance — mirrors
 * finance/FinanceCompanyPage.tsx. */
export default function SupplierFinanceCompanyPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const init = useMemo<FiltersValue>(() => ({ preset: "ytd", ...presetRange("ytd") }), []);
  const [filters, setFilters] = useState<FiltersValue>(init);

  const canViewSettlements = hasPermission("supplier_settlements:view");

  const load = useCallback(() => {
    if (!id) return;
    supplierFinanceApi.company(id, { from: filters.from, to: filters.to })
      .then(setData)
      .catch((e: ApiError) => setError(e.message));
  }, [id, filters.from, filters.to]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const onChange = (v: FiltersValue) => {
    const range = v.preset === "custom" ? { from: v.from, to: v.to } : presetRange(v.preset);
    setFilters({ ...v, ...range });
  };

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label="Breadcrumb">
            <span><Link to="/procurement/finance" style={{ color: "var(--ink-3)", textDecoration: "none" }}>{t("supplierFinance.title", "Supplier finance")}</Link></span>
            <span style={{ color: "var(--ink-2)", fontWeight: 600 }}>{data?.company.name ?? "…"}</span>
          </nav>
          <h1>{data?.company.name ?? t("supplierFinance.title", "Supplier finance")}</h1>
        </div>
        <div className="page-actions">
          <FinanceFilters value={filters} markets={[]} countries={[]} onChange={onChange} />
          {id && data?.bothSidesVisible && (
            <Link to={`/procurement/finance/${id}/combined`} className="btn">
              {t("combinedLedger.title", "Both sides")}
            </Link>
          )}
          {id && canViewSettlements && (
            <ExportButton
              path={`/exports/supplier-settlements.csv?companyId=${id}`}
              label={t("settlements.exportCsv", "CSV")}
              filename="supplier-settlements.csv"
            />
          )}
        </div>
      </div>

      {error && <div className="empty">{error}</div>}
      {data ? (
        <FinanceDetail data={data} strana="dobavljac" />
      ) : (
        <TableSkeleton rows={8} cols={6} label={t("common.loading", "Učitavam…")} />
      )}
    </div>
  );
}
